import { useState } from "react";
import { CopyButton } from "../shared";

const MCP_URL = "https://dev-api.horuswallet.com/mcp";
const SKILL_URL = "https://dev.horuswallet.com/skill.md";

function getPlaceholderKey(apiKey) {
  return apiKey || "horus_ak_YOUR_KEY";
}

export default function PlatformTabs({ latestKey }) {
  const [activeTab, setActiveTab] = useState("claude-desktop");
  const key = getPlaceholderKey(latestKey);
  const hasKey = !!latestKey;

  const tabs = [
    { id: "claude-desktop", label: "Claude Desktop" },
    { id: "claude-code", label: "Claude Code" },
    { id: "vscode", label: "VS Code Copilot" },
    { id: "cursor", label: "Cursor" },
    { id: "codex", label: "Codex" },
    { id: "opencode", label: "OpenCode" },
    { id: "chatgpt", label: "ChatGPT" },
  ];

  const claudeDesktopConfig = JSON.stringify(
    {
      mcpServers: {
        "horus-wallet": {
          url: MCP_URL,
          headers: { Authorization: `Bearer ${key}` },
        },
      },
    },
    null,
    2
  );

  const vscodeConfig = JSON.stringify(
    {
      servers: {
        "horus-wallet": {
          url: MCP_URL,
          headers: { Authorization: `Bearer ${key}` },
        },
      },
    },
    null,
    2
  );

  const cursorConfig = JSON.stringify(
    {
      mcpServers: {
        "horus-wallet": {
          url: MCP_URL,
          headers: { Authorization: `Bearer ${key}` },
        },
      },
    },
    null,
    2
  );

  const openCodeConfig = JSON.stringify(
    {
      mcpServers: {
        "horus-wallet": {
          type: "streamable-http",
          url: MCP_URL,
          headers: { Authorization: `Bearer ${key}` },
        },
      },
    },
    null,
    2
  );

  const claudeCodeCmd = `claude mcp add --transport http horus-wallet ${MCP_URL} --header "Authorization: Bearer ${key}"`;

  const codexToml = `[mcp_servers.horus-wallet]
url = "${MCP_URL}"

[mcp_servers.horus-wallet.http_headers]
Authorization = "Bearer ${key}"`;

  const codexTomlEnv = `[mcp_servers.horus-wallet]
url = "${MCP_URL}"
bearer_token_env_var = "HORUS_API_KEY"`;

  const openApiSchema = `openapi: "3.1.0"
info:
  title: Horus Wallet MCP
  version: "1.0"
servers:
  - url: ${MCP_URL}
paths:
  /:
    post:
      operationId: mcpRequest
      summary: Send MCP tool calls to Horus Wallet
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
      responses:
        "200":
          description: MCP response`;

  const noKeyNote = !hasKey && (
    <div className="mcp-warning-box" style={{ marginBottom: "1rem" }}>
      Generate an API key in the Overview tab first, then come back here.
    </div>
  );

  return (
    <div className="mcp-platform-tabs">
      <div className="mcp-tab-bar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`mcp-tab-btn ${activeTab === tab.id ? "mcp-tab-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mcp-tab-content">
        {activeTab === "claude-desktop" && (
          <div className="mcp-setup-steps">
            {noKeyNote}
            <ol className="mcp-ol">
              <li>
                Open:{" "}
                <code className="mcp-inline-code">
                  %APPDATA%\Claude\claude_desktop_config.json
                </code>
              </li>
              <li>Paste this config:</li>
            </ol>
            <pre className="mcp-code-block">{claudeDesktopConfig}</pre>
            <CopyButton text={claudeDesktopConfig} label="Copy config" />
            <ol className="mcp-ol" start={3}>
              <li>Restart Claude Desktop</li>
              <li>
                Look for the <strong>🔨</strong> icon in the chat input
              </li>
            </ol>
          </div>
        )}

        {activeTab === "claude-code" && (
          <div className="mcp-setup-steps">
            {noKeyNote}
            <p>Run this command in your terminal:</p>
            <pre className="mcp-code-block">{claudeCodeCmd}</pre>
            <CopyButton text={claudeCodeCmd} label="Copy command" />
          </div>
        )}

        {activeTab === "vscode" && (
          <div className="mcp-setup-steps">
            {noKeyNote}
            <ol className="mcp-ol">
              <li>
                Create <code className="mcp-inline-code">.vscode/mcp.json</code>{" "}
                in your workspace:
              </li>
            </ol>
            <pre className="mcp-code-block">{vscodeConfig}</pre>
            <CopyButton text={vscodeConfig} label="Copy config" />
            <ol className="mcp-ol" start={2}>
              <li>
                Open Copilot Chat → select <strong>Agent mode</strong>
              </li>
            </ol>
          </div>
        )}

        {activeTab === "cursor" && (
          <div className="mcp-setup-steps">
            {noKeyNote}
            <ol className="mcp-ol">
              <li>
                Create{" "}
                <code className="mcp-inline-code">~/.cursor/mcp.json</code>:
              </li>
            </ol>
            <pre className="mcp-code-block">{cursorConfig}</pre>
            <CopyButton text={cursorConfig} label="Copy config" />
          </div>
        )}

        {activeTab === "codex" && (
          <div className="mcp-setup-steps">
            {noKeyNote}
            <p>
              Edit{" "}
              <code className="mcp-inline-code">~/.codex/config.toml</code>:
            </p>
            <pre className="mcp-code-block">{codexToml}</pre>
            <CopyButton text={codexToml} label="Copy config" />

            <p style={{ marginTop: "1rem" }}>
              Or use an env var (more secure):
            </p>
            <pre className="mcp-code-block">{codexTomlEnv}</pre>
            <CopyButton text={codexTomlEnv} label="Copy config" />

            <ol className="mcp-ol" style={{ marginTop: "1rem" }}>
              <li>
                Set env:{" "}
                <code className="mcp-inline-code">
                  export HORUS_API_KEY="{key}"
                </code>
              </li>
              <li>
                In Codex, type <code className="mcp-inline-code">/mcp</code> to
                verify
              </li>
              <li>Try: &ldquo;What's my wallet balance?&rdquo;</li>
            </ol>

            <p style={{ marginTop: "1rem" }}>
              Add{" "}
              <code className="mcp-inline-code">AGENTS.md</code> for better
              tool usage:
            </p>
            <pre className="mcp-code-block">
              {`For Horus Wallet MCP tools, follow: ${SKILL_URL}`}
            </pre>
            <CopyButton
              text={`For Horus Wallet MCP tools, follow: ${SKILL_URL}`}
              label="Copy"
            />
          </div>
        )}

        {activeTab === "opencode" && (
          <div className="mcp-setup-steps">
            {noKeyNote}
            <p>
              Add to your OpenCode MCP config (
              <code className="mcp-inline-code">opencode.json</code> or
              settings):
            </p>
            <pre className="mcp-code-block">{openCodeConfig}</pre>
            <CopyButton text={openCodeConfig} label="Copy config" />
            <ol className="mcp-ol" style={{ marginTop: "1rem" }}>
              <li>Restart OpenCode or reload the config</li>
              <li>Try: &ldquo;What's my wallet balance?&rdquo;</li>
            </ol>
          </div>
        )}

        {activeTab === "chatgpt" && (
          <div className="mcp-setup-steps">
            {noKeyNote}
            <ol className="mcp-ol">
              <li>
                Go to ChatGPT → <strong>Explore GPTs</strong> →{" "}
                <strong>Create a GPT</strong>
              </li>
              <li>
                <strong>Actions</strong> → <strong>Create new action</strong>
              </li>
              <li>Paste this OpenAPI schema:</li>
            </ol>
            <pre className="mcp-code-block">{openApiSchema}</pre>
            <CopyButton text={openApiSchema} label="Copy schema" />
            <ol className="mcp-ol" start={4}>
              <li>
                Set auth: <strong>Bearer Token</strong> → paste your API key
              </li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
